import { useMemo, useState } from "react";
import { BackLink } from "../ui/BackLink";
import { Avatar } from "../ui/Avatar";
import { useContactsToTriage, type InboxItem } from "../data";
import { dismissContactBacklog, dismissContacts, resolveContactTriage, startOfToday } from "../actions";
import { IcCheck, IcPeople, IcSearch, IcTrash, IcUserPlus } from "../ui/icons";

export function TriagePage() {
  const { data, loading, error } = useContactsToTriage(200);
  const [term, setTerm] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [clearPreview, setClearPreview] = useState<{ wouldDismiss: number; keptNewer: number } | null>(null);
  const [clearBusy, setClearBusy] = useState(false);

  const q = term.trim().toLowerCase();
  const rows = useMemo(() => {
    const match = (i: InboxItem) => {
      if (hidden.has(i.id)) return false;
      if (q === "") return true;
      const e = i.evidence ?? {};
      return [e.fullName, e.organization, e.note, ...(e.phones ?? []), ...(e.labels ?? [])].some(
        (v) => typeof v === "string" && v.toLowerCase().includes(q),
      );
    };
    return data.filter(match);
  }, [data, q, hidden]);

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.id));

  async function removePicked() {
    const ids = [...picked];
    if (ids.length === 0) return;
    if (
      !confirm(
        `להסיר ${ids.length} אנשי קשר מהרשימה? הם לא ייכנסו ל-CRM ולא נציע אותם שוב. באנשי הקשר בגוגל לא נמחק כלום.`,
      )
    )
      return;

    setBusy(true);
    setNote(null);
    try {
      let done = 0;
      for (let i = 0; i < ids.length; i += 200) {
        const r = await dismissContacts(ids.slice(i, i + 200));
        done += r.dismissed;
      }
      setPicked(new Set());
      setNote(`הסרנו ${done}.`);
    } catch {
      setNote("לא הצלחנו להסיר. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  async function quickAdd(item: InboxItem, e: React.MouseEvent) {
    e.stopPropagation();
    setAddingId(item.id);
    setNote(null);
    setHidden((s) => new Set(s).add(item.id));
    try {
      const r = await resolveContactTriage(item.id, "add", [], "");
      if (r.status === "linked") setNote("איש הקשר כבר היה במערכת.");
      else if (r.status === "created") setNote(`נוסף ל-CRM: ${item.evidence?.fullName ?? ""}`);
      else if (r.status === "ambiguous") {
        setHidden((s) => {
          const n = new Set(s);
          n.delete(item.id);
          return n;
        });
        setNote("נמצאה התנגשות בטלפון/אימייל עם איש קשר קיים.");
      }
    } catch {
      setHidden((s) => {
        const n = new Set(s);
        n.delete(item.id);
        return n;
      });
      setNote("לא הצלחנו להוסיף. נסו שוב.");
    } finally {
      setAddingId(null);
    }
  }

  const when = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? null
      : new Intl.DateTimeFormat("he-IL", { month: "short", year: "numeric" }).format(d);
  };

  return (
    <div className="screen">
      <header className="hdr">
        <BackLink fallback="/more" label="חזרה" />
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>
              <IcPeople size={19} /> מיון אנשי קשר מגוגל
            </h1>
            <p className="sub">
              {loading
                ? "טוענים…"
                : picked.size > 0
                  ? `${picked.size} מסומנים מתוך ${rows.length}`
                  : `${rows.length} ממתינים למיון`}
            </p>
          </div>
        </div>

        <div className="searchbar">
          <IcSearch size={18} />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="סינון לפי שם, טלפון או תווית…"
            autoComplete="off"
          />
        </div>

        {rows.length > 0 && (
          <div className="triage-bar">
            <button
              className="faint"
              onClick={() =>
                setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.id)))
              }
            >
              {allPicked ? "ניקוי הבחירה" : `סמנו את כל ה-${rows.length}`}
            </button>
            {picked.size > 0 && (
              <button className="btn btn-sm danger" disabled={busy} onClick={removePicked}>
                <IcTrash size={15} /> הסירו {picked.size}
              </button>
            )}
          </div>
        )}
      </header>

      <div className="body">
        {note && <div className="notice info">{note}</div>}

        {error && (
          <div className="notice error">
            לא הצלחנו לטעון את הרשימה. זו תקלה, לא סימן שאין אנשי קשר. נסו לרענן את הדף.
          </div>
        )}

        {rows.length > 0 && (
          <div className="card" style={{ marginBottom: 12 }}>
            {!clearing ? (
              <div style={{ padding: 12 }}>
                <button
                  className="btn btn-wide"
                  onClick={async () => {
                    setClearing(true);
                    setClearPreview(null);
                    try {
                      const r = await dismissContactBacklog({ beforeIso: startOfToday(), dryRun: true });
                      setClearPreview({ wouldDismiss: r.wouldDismiss ?? 0, keptNewer: r.keptNewer ?? 0 });
                    } catch (e) {
                      setNote(e instanceof Error ? e.message : "לא הצלחנו לבדוק. נסו שוב.");
                      setClearing(false);
                    }
                  }}
                >
                  ניקוי כל הרשימה הישנה מלפני היום
                </button>
              </div>
            ) : (
              <div style={{ padding: 12 }}>
                <p className="faint" style={{ marginTop: 0 }}>
                  {clearPreview === null
                    ? "בודקים…"
                    : clearPreview.wouldDismiss === 0
                      ? "אין כרטיסים ישנים לנקות. כל מה שמוצג כאן הגיע היום."
                      : `נסמן ${clearPreview.wouldDismiss} כרטיסים מלפני היום כמטופלים, ולא נציע אותם שוב. ${clearPreview.keptNewer > 0 ? `${clearPreview.keptNewer} שהגיעו היום יישארו לכם להחלטה.` : ""}`}
                </p>
                <div className="btn-row">
                  {clearPreview !== null && clearPreview.wouldDismiss > 0 && (
                    <button
                      className="btn btn-primary"
                      disabled={clearBusy}
                      onClick={async () => {
                        setClearBusy(true);
                        try {
                          const r = await dismissContactBacklog({ beforeIso: startOfToday() });
                          setNote(`ניקינו ${r.dismissed} כרטיסים ישנים.`);
                          setClearing(false);
                        } catch (e) {
                          setNote(e instanceof Error ? e.message : "לא הצלחנו לנקות. נסו שוב.");
                        } finally {
                          setClearBusy(false);
                        }
                      }}
                    >
                      {clearBusy ? "מנקים…" : "נקו עכשיו"}
                    </button>
                  )}
                  <button className="btn" disabled={clearBusy} onClick={() => setClearing(false)}>
                    ביטול
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="empty">
            <div className="empty-ico tint-green">
              <IcCheck size={26} />
            </div>
            <h3>אין אנשי קשר שממתינים למיון</h3>
            <p>כשנמצא בגוגל איש קשר שעוד לא במערכת, הוא יופיע כאן.</p>
          </div>
        )}

        {rows.length > 0 && (
          <div className="card">
            {rows.map((item, i) => {
              const e = item.evidence ?? {};
              const on = picked.has(item.id);
              const age = when(e.sourceUpdatedAt);
              return (
                <div
                  key={item.id}
                  className="list-row triage-row"
                  onClick={() => toggle(item.id)}
                  style={{ borderBottom: i === rows.length - 1 ? "0" : undefined }}
                >
                  <span className={on ? "selbox on" : "selbox"} aria-hidden="true">
                    {on && <IcCheck size={13} />}
                  </span>
                  <Avatar name={e.fullName ?? "?"} size={34} />
                  <span className="grow">
                    <span className="name-row">
                      {e.fullName ?? <span className="faint">ללא שם</span>}
                    </span>
                    <span className="muted mono" dir="ltr" style={{ display: "block", textAlign: "start" }}>
                      {(e.phones ?? [])[0] ?? (e.emails ?? [])[0] ?? "—"}
                    </span>
                    <span className="faint" style={{ display: "block" }}>
                      {[age, (e.labels ?? [])[0], e.organization].filter(Boolean).join(" · ") || " "}
                    </span>
                  </span>
                  <button
                    className="btn btn-sm"
                    disabled={addingId === item.id}
                    onClick={(ev) => quickAdd(item, ev)}
                  >
                    <IcUserPlus size={14} /> הוסף
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
